import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { userAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Camera, Upload, Save } from 'lucide-react';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [success, setSuccess] = React.useState(null);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await userAPI.getProfile();
      const profileData = res.data.data?.user || res.data.data;
      setProfile(profileData);
      setImagePreview(profileData?.profileImage || null);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchProfile();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile || !profile?.id) return;

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);
      const formData = new FormData();
      formData.append('profileImage', imageFile);
      
      const response = await userAPI.uploadProfileImage(profile.id, formData);
      
      // Get the new profile image URL from response
      const newProfileImageUrl = response.data?.data?.profileImage;
      
      if (newProfileImageUrl) {
        // Update image preview immediately
        setImagePreview(newProfileImageUrl);
        
        // Update profile state with new image URL
        setProfile(prev => ({
          ...prev,
          profileImage: newProfileImageUrl
        }));
        
        // Update AuthContext user state so other components show the new image
        if (user && user.id === profile.id) {
          updateUser({ profileImage: newProfileImageUrl });
        }
      }
      
      setSuccess('Profile image updated successfully');
      setImageFile(null);
      
      // Refetch profile to ensure we have the latest data
      await fetchProfile();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to upload image';
      setError(errorMsg);
      console.error('Profile image upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error && !profile) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchProfile}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Profile</h2>
        <p className="text-gray-600 mt-1">Manage your profile information</p>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Profile Picture</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <Avatar className="w-32 h-32">
              {(imagePreview || profile?.profileImage) && (
                <AvatarImage 
                  src={imagePreview || profile?.profileImage} 
                  alt={`${profile?.firstName} ${profile?.lastName}`} 
                />
              )}
              <AvatarFallback className="text-2xl">
                {profile?.firstName?.[0] || ''}{profile?.lastName?.[0] || ''}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-4">
              <div>
                <Label htmlFor="profileImage">Upload New Profile Picture</Label>
                <Input
                  id="profileImage"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="mt-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Recommended: Square image, max 5MB (JPG, PNG, GIF)
                </p>
              </div>
              {imageFile && (
                <Button
                  onClick={handleImageUpload}
                  disabled={uploading}
                  className="flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Upload className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Profile Picture
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm text-gray-600">Name</Label>
              <p className="text-base font-medium">{profile?.firstName} {profile?.lastName}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Email</Label>
              <p className="text-base font-medium">{profile?.email || '-'}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Role</Label>
              <p className="text-base font-medium capitalize">{profile?.role || '-'}</p>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Phone</Label>
              <p className="text-base font-medium">{profile?.phone || '-'}</p>
            </div>
            {profile?.userCode && (
              <div>
                <Label className="text-sm text-gray-600">User Code</Label>
                <p className="text-base font-medium">{profile.userCode}</p>
              </div>
            )}
            {profile?.studentId && (
              <div>
                <Label className="text-sm text-gray-600">Student ID</Label>
                <p className="text-base font-medium">{profile.studentId}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
